/**
 * Routing policy: the two lane orders (a code constant), shown read-only with
 * how many workspaces follow each. A lane switched off in Lanes is struck
 * through here, because the router skips it.
 */

import { Fragment } from "react";
import type { Lane, LaneState, RoutedWorkspace, RoutingPolicy } from "./types.js";
import { LANE_NAMES, PIN_ONLY_COPY } from "./format.js";
import { PRINT_CARD_CLASS, PRINT_CARD_STYLE } from "./styles.js";
import { LaneBadges } from "./LaneBadges.js";

interface RoutingPolicyCardProps {
  policy: RoutingPolicy;
  lanes: LaneState[];
  workspaces: RoutedWorkspace[];
  /** Every workspace, from GET /api/admin/workspaces. */
  totalWorkspaces: number;
}

function LaneChip({ lane, index, serving }: { lane: Lane; index: number; serving: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-2.5 py-1 text-xs ${
        serving ? "" : "opacity-45 line-through"
      }`}
      style={{
        borderColor: "var(--print-border)",
        background: "rgba(var(--glass-rgb), .6)",
        color: "var(--text-primary)",
      }}
      data-serving={serving ? "true" : "false"}
    >
      <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>
        {index + 1}
      </span>
      {LANE_NAMES[lane]}
      <LaneBadges lane={lane} />
      {!serving && <span className="sr-only"> (off)</span>}
    </span>
  );
}

export function RoutingPolicyCard({
  policy,
  lanes,
  workspaces,
  totalWorkspaces,
}: RoutingPolicyCardProps) {
  const serving = new Map(lanes.map((l) => [l.lane, l.serving]));
  const usOnly = workspaces.filter((w) => w.dataResidency === "us").length;
  const unrestricted = Math.max(0, totalWorkspaces - usOnly);

  const rows: Array<{ name: string; hint: string; order: Lane[]; count: number }> = [
    {
      name: "Unrestricted",
      hint: "Any lane may serve. Every new workspace starts here.",
      order: policy.unrestricted,
      count: unrestricted,
    },
    {
      name: "US only",
      hint: "US-hosted lanes only, fail closed. Set per workspace below.",
      order: policy.us,
      count: usOnly,
    },
  ];

  return (
    <section aria-label="Routing policy" className={PRINT_CARD_CLASS} style={PRINT_CARD_STYLE}>
      <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
        Routing policy
      </h3>
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        The lane order each policy walks, the same for every tier. Set in code, shown here.
      </p>
      {(policy.pinOnly?.length ?? 0) > 0 && (
        <p className="text-xs mb-3" style={{ color: "var(--text-tertiary)" }} data-testid="policy-pin-only">
          {PIN_ONLY_COPY}
        </p>
      )}
      <div className="flex flex-col">
        {rows.map((row) => (
          <div
            key={row.name}
            data-policy={row.name}
            className="grid grid-cols-1 items-center gap-2 py-2 md:grid-cols-[180px_1fr_auto]"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            <div>
              <div className="text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>
                {row.name}
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                {row.hint}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {row.order.map((lane, index) => (
                <Fragment key={lane}>
                  {index > 0 && (
                    <span aria-hidden style={{ color: "var(--text-tertiary)" }}>
                      →
                    </span>
                  )}
                  <LaneChip lane={lane} index={index} serving={serving.get(lane) ?? true} />
                </Fragment>
              ))}
            </div>
            <div
              className="text-xs md:text-right"
              style={{ color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}
            >
              {row.count} {row.count === 1 ? "workspace" : "workspaces"}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
